import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  People,
  AdminPanelSettings,
  Security,
  Support,
  Settings,
  SupervisorAccount,
  Refresh,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Schedule,
  PersonAdd,
  Login,
} from "@mui/icons-material";

interface UserStats {
  total_users: number;
  active_users: number;
  staff_users: number;
  superusers: number;
}

interface SystemHealth {
  status: "healthy" | "warning" | "error";
  database: "connected" | "slow" | "error";
  cache: "active" | "inactive" | "error";
  celery: "running" | "stopped" | "error" | "unknown";
}

interface ActivityItem {
  id: string;
  type:
    | "user_signup"
    | "user_login"
    | "admin_action"
    | "user_action"
    | "system_event";
  message: string;
  user?: string;
  timestamp: string;
}

export const AdminDashboard: React.FC = () => {
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [healthError, setHealthError] = useState(false);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  useEffect(() => {
    fetchAllData();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchAllData();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchAllData = async () => {
    await Promise.all([
      fetchUserStats(),
      fetchSystemHealth(),
      fetchRecentActivity(),
    ]);
    setLastRefresh(new Date());
  };

  const fetchUserStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch("/users/admin/users/stats/", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfToken(),
        },
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user stats: ${response.status}`);
      }

      const data = await response.json();
      setUserStats(data);
    } catch {
      setError("Failed to load user statistics");
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemHealth = async () => {
    try {
      const response = await fetch("/users/admin/health/", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfToken(),
        },
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch system health: ${response.status}`);
      }

      const data = await response.json();
      setSystemHealth(data);
      setHealthError(false);
    } catch {
      setSystemHealth(null);
      setHealthError(true);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await fetch("/users/admin/activity/", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfToken(),
        },
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch recent activity: ${response.status}`);
      }

      const data = await response.json();
      setRecentActivity(data.activities || []);
    } catch {
      // Fallback to empty array on error
      setRecentActivity([]);
    }
  };

  const getCsrfToken = (): string => {
    const csrfToken = (
      document.querySelector("[name=csrfmiddlewaretoken]") as HTMLInputElement
    )?.value;
    if (csrfToken) return csrfToken;

    const cookieValue = document.cookie
      .split("; ")
      .find((row) => row.startsWith("csrftoken="))
      ?.split("=")[1];
    return cookieValue || "";
  };

  const getHealthColor = (status: string) => {
    switch (status) {
      case "healthy":
      case "connected":
      case "active":
      case "running":
        return "success";
      case "warning":
      case "slow":
      case "inactive":
        return "warning";
      case "error":
      case "stopped":
        return "error";
      default:
        return "default";
    }
  };

  const getActivityIcon = (type: ActivityItem["type"]) => {
    switch (type) {
      case "user_signup":
        return <PersonAdd color="success" />;
      case "user_login":
        return <Login color="primary" />;
      case "admin_action":
        return <AdminPanelSettings color="warning" />;
      case "user_action":
        return <People color="info" />;
      case "system_event":
        return <Settings color="info" />;
      default:
        return <CheckCircle />;
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4" gutterBottom>
            Admin Dashboard
          </Typography>
          <Tooltip title="Refreshing...">
            <IconButton disabled>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4" gutterBottom>
            Admin Dashboard
          </Typography>
          <Tooltip title="Retry">
            <IconButton onClick={fetchAllData}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchAllData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Refresh */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <div>
          <Typography variant="h4" gutterBottom>
            Admin Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </Typography>
        </div>
        <Tooltip title="Refresh Dashboard">
          <IconButton onClick={fetchAllData} color="primary">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* System Health Status */}
      {systemHealth && (
        <Alert
          severity={
            getHealthColor(systemHealth.status) as
              | "success"
              | "warning"
              | "error"
          }
          sx={{ mb: 3 }}
          icon={
            systemHealth.status === "healthy" ? (
              <CheckCircle />
            ) : systemHealth.status === "warning" ? (
              <Warning />
            ) : (
              <ErrorIcon />
            )
          }
        >
          <Typography variant="body2">
            System Status: <strong>{systemHealth.status.toUpperCase()}</strong>
            {systemHealth.status === "healthy" && " - Database and cache checks passed"}
          </Typography>
        </Alert>
      )}
      {healthError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          System health could not be loaded.
        </Alert>
      )}

      {/* User Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <People sx={{ mr: 1, color: "readable.primary" }} />
                <Typography variant="h6">Total Users</Typography>
              </Box>
              <Typography variant="h4">
                {userStats?.total_users || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All registered users
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <People sx={{ mr: 1, color: "statusText.ready" }} />
                <Typography variant="h6">Active Users</Typography>
              </Box>
              <Typography variant="h4">
                {userStats?.active_users || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active accounts
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <AdminPanelSettings sx={{ mr: 1, color: "secondary.main" }} />
                <Typography variant="h6">Staff Users</Typography>
              </Box>
              <Typography variant="h4">
                {userStats?.staff_users || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Administrative users
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <SupervisorAccount sx={{ mr: 1, color: "statusText.inFlight" }} />
                <Typography variant="h6">Superusers</Typography>
              </Box>
              <Typography variant="h4">{userStats?.superusers || 0}</Typography>
              <Typography variant="body2" color="text.secondary">
                Full system access
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* System Health Details */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, height: "fit-content" }}>
            <Typography variant="h6" gutterBottom>
              System Health
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2">Database</Typography>
                <Chip
                  size="small"
                  label={systemHealth?.database || "Unknown"}
                  color={getHealthColor(systemHealth?.database || "unknown")}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2">Cache</Typography>
                <Chip
                  size="small"
                  label={systemHealth?.cache || "Unknown"}
                  color={getHealthColor(systemHealth?.cache || "unknown")}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2">Background Jobs</Typography>
                <Chip
                  size="small"
                  label={systemHealth?.celery || "Unknown"}
                  color={getHealthColor(systemHealth?.celery || "unknown")}
                />
              </Box>
              {systemHealth?.celery === "unknown" && (
                <Typography variant="body2" color="text.secondary">
                  Background jobs have not been checked.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">Recent Activity</Typography>
              <Chip
                icon={<Schedule />}
                label="Live"
                color="success"
                size="small"
                variant="outlined"
              />
            </Box>
            <List sx={{ maxHeight: 300, overflow: "auto" }}>
              {recentActivity.map((activity, index) => (
                <React.Fragment key={activity.id}>
                  <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                      {getActivityIcon(activity.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" fontWeight="medium">
                            {activity.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimeAgo(activity.timestamp)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        activity.user && (
                          <Typography variant="caption" color="text.secondary">
                            {activity.user}
                          </Typography>
                        )
                      }
                    />
                  </ListItem>
                  {index < recentActivity.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            {recentActivity.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No recent activity to display
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="outlined"
              startIcon={<People />}
              href="/superuser/users/"
              fullWidth
              sx={{ py: 1.5 }}
            >
              Manage Users
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="outlined"
              startIcon={<Security />}
              href="/superuser/impersonate/"
              fullWidth
              sx={{ py: 1.5 }}
            >
              User Impersonation
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="outlined"
              startIcon={<Support />}
              href="/support/"
              fullWidth
              sx={{ py: 1.5 }}
            >
              Support Center
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Button
              variant="outlined"
              startIcon={<AdminPanelSettings />}
              href="/admin/"
              target="_blank"
              fullWidth
              sx={{ py: 1.5 }}
            >
              Django Admin
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default AdminDashboard;
